declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
        prefill?: { name?: string; email?: string };
      }) => void;
    };
  }
}

import scheduleModalHtml from "../../public/partials/schedule-modal.html";

const CALENDLY_INLINE_URL =
  "https://calendly.com/oleg-lazarev/get-started?hide_event_type_details=1&hide_gdpr_banner=1";
const CALENDLY_WIDGET_SRC =
  "https://assets.calendly.com/assets/external/widget.js";

let calendlyScriptPromise: Promise<void> | null = null;

function loadCalendlyWidgetScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.Calendly) return Promise.resolve();
  if (calendlyScriptPromise) return calendlyScriptPromise;
  calendlyScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${CALENDLY_WIDGET_SRC}"]`
    );
    const onReady = () => {
      if (window.Calendly) resolve();
      else reject(new Error("Calendly widget loaded but API missing"));
    };
    if (existing) {
      if (window.Calendly) {
        resolve();
        return;
      }
      existing.addEventListener("load", onReady);
      existing.addEventListener("error", () =>
        reject(new Error("Calendly script failed to load"))
      );
      return;
    }
    const s = document.createElement("script");
    s.src = CALENDLY_WIDGET_SRC;
    s.async = true;
    s.onload = onReady;
    s.onerror = () => reject(new Error("Calendly script failed to load"));
    document.head.appendChild(s);
  });
  return calendlyScriptPromise;
}

function isCalendlyEventScheduled(e: MessageEvent): boolean {
  if (e.origin !== "https://calendly.com") return false;
  const d = e.data;
  if (typeof d !== "object" || d === null || !("event" in d)) return false;
  return (d as { event: string }).event === "calendly.event_scheduled";
}

(async () => {
  document.body.insertAdjacentHTML("beforeend", scheduleModalHtml);

  const modal = document.getElementById("schedule-modal");
  if (!modal) return;
  const modalEl: HTMLElement = modal;

  const heroScheduleBtn = document.getElementById("schedule-call-open");
  const closeBtn = modalEl.querySelector<HTMLButtonElement>("#schedule-modal-close");
  const scrim = modalEl.querySelector("#schedule-modal-scrim");
  const heroForm = document.getElementById(
    "form-hero-schedule"
  ) as HTMLFormElement | null;
  const getStartedForm = document.getElementById(
    "form-get-started"
  ) as HTMLFormElement | null;
  const heroEmail = document.getElementById(
    "hero-email-input"
  ) as HTMLInputElement | null;
  const featuresEmail = document.getElementById(
    "features-get-started-email"
  ) as HTMLInputElement | null;
  const scheduleBody = modalEl.querySelector<HTMLElement>("#schedule-call-form");
  const successEl = modalEl.querySelector<HTMLElement>("#schedule-modal-success");

  const closeHeaderNavIfOpen = () => {
    const headerEl = document.getElementById("header");
    if (!headerEl?.classList.contains("dex-header--nav-open")) return;
    headerEl.classList.remove("dex-header--nav-open");
    document.body.classList.remove("nav-open");
    const burger = document.getElementById("header-burger");
    const drawer = document.getElementById("header-mobile");
    const headerScrim = document.getElementById("header-scrim");
    if (burger) {
      burger.setAttribute("aria-expanded", "false");
      burger.setAttribute("aria-label", "Open menu");
    }
    if (drawer) drawer.setAttribute("aria-hidden", "true");
    if (headerScrim) headerScrim.setAttribute("aria-hidden", "true");
  };

  let lockedScrollY = 0;
  let lastFocus: HTMLElement | null = null;
  let activeSourceEmailInput: HTMLInputElement | null = null;

  async function mountCalendly(prefillEmail?: string): Promise<void> {
    const host = modalEl.querySelector<HTMLElement>(
      "#schedule-calendly-inline-host"
    );
    if (!host) return;
    try {
      await loadCalendlyWidgetScript();
      host.replaceChildren();
      window.Calendly?.initInlineWidget({
        url: host.getAttribute("data-url") ?? CALENDLY_INLINE_URL,
        parentElement: host,
        ...(prefillEmail ? { prefill: { email: prefillEmail } } : {}),
      });
    } catch (err) {
      console.error(err);
    }
  }

  const closeModal = () => {
    modalEl.classList.remove("is-open");
    modalEl.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("schedule-modal-open");
    document.body.classList.remove("schedule-modal-open");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    window.scrollTo(0, lockedScrollY);
    if (lastFocus && typeof lastFocus.focus === "function") {
      lastFocus.focus();
    }
    lastFocus = null;
  };

  /* Safari snapshots the page as the user leaves it; an open modal in that
     snapshot ghost-flashes over the next visit while the new load cross-fades
     in, and a bfcache restore brings it back open for real. Close it on the
     way out and after any bfcache restore. */
  const closeIfOpen = () => {
    if (modalEl.classList.contains("is-open")) closeModal();
  };
  window.addEventListener("pagehide", closeIfOpen);
  window.addEventListener("pageshow", (e: PageTransitionEvent) => {
    if (e.persisted) closeIfOpen();
  });

  let thankYouShown = false;

  const showSuccess = () => {
    if (thankYouShown) return;
    if (!scheduleBody || !successEl) return;
    thankYouShown = true;
    scheduleBody.hidden = true;
    if (activeSourceEmailInput) {
      activeSourceEmailInput.value = "";
      activeSourceEmailInput.dispatchEvent(new Event("input", { bubbles: true }));
      activeSourceEmailInput = null;
    }
    const titleEl = modalEl.querySelector<HTMLElement>(".dex-schedule-modal__title");
    if (titleEl) titleEl.hidden = true;
    successEl.hidden = false;
    setTimeout(() => {
      closeModal();
      scheduleBody.hidden = false;
      successEl.hidden = true;
      if (titleEl) titleEl.hidden = false;
      modalEl
        .querySelector("#schedule-calendly-inline-host")
        ?.replaceChildren();
    }, 5000);
  };

  const openModal = (emailSource?: HTMLInputElement | null) => {
    thankYouShown = false;
    closeHeaderNavIfOpen();
    lastFocus = document.activeElement as HTMLElement | null;
    const fromSrc = emailSource?.value?.trim();
    const fromHero = heroEmail?.value?.trim();
    const prefillEmail = fromSrc || fromHero || undefined;
    if (fromSrc) {
      activeSourceEmailInput = emailSource ?? null;
    } else if (fromHero) {
      activeSourceEmailInput = heroEmail;
    } else {
      activeSourceEmailInput = null;
    }

    lockedScrollY = window.scrollY;
    document.documentElement.classList.add("schedule-modal-open");
    document.body.classList.add("schedule-modal-open");
    document.body.style.position = "fixed";
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    modalEl.classList.add("is-open");
    modalEl.setAttribute("aria-hidden", "false");
    void mountCalendly(prefillEmail);
    requestAnimationFrame(() => {
      closeBtn?.focus();
    });
  };

  const openModalIfHeroEmailValid = () => {
    if (!heroEmail) return;
    if (!heroEmail.checkValidity()) {
      heroEmail.reportValidity();
      return;
    }
    openModal();
  };

  heroScheduleBtn?.addEventListener("click", () =>
    openModalIfHeroEmailValid()
  );

  document.addEventListener("click", (e) => {
    const trigger = (e.target as HTMLElement).closest(
      "[data-open-schedule-modal]"
    );
    if (trigger) openModal();
  });
  closeBtn?.addEventListener("click", () => closeModal());
  scrim?.addEventListener("click", () => closeModal());

  heroForm?.addEventListener("submit", (e) => {
    e.preventDefault();
    const em = heroForm.querySelector("#hero-email-input") as
      | HTMLInputElement
      | null;
    if (em && !em.checkValidity()) {
      em.reportValidity();
      return;
    }
    openModal(em);
  });

  getStartedForm?.addEventListener("submit", (e) => {
    e.preventDefault();
    if (featuresEmail && !featuresEmail.checkValidity()) {
      featuresEmail.reportValidity();
      return;
    }
    openModal(featuresEmail);
  });

  const aboutCtaBtn = document.getElementById("about-cta-btn");
  const aboutEmailIn = document.getElementById(
    "about-email"
  ) as HTMLInputElement | null;
  aboutCtaBtn?.addEventListener("click", () => {
    if (!aboutEmailIn) return;
    if (!aboutEmailIn.checkValidity()) {
      aboutEmailIn.reportValidity();
      return;
    }
    openModal(aboutEmailIn);
  });

  window.addEventListener("message", (e: MessageEvent) => {
    if (!modalEl.classList.contains("is-open")) return;
    if (!isCalendlyEventScheduled(e)) return;
    showSuccess();
  });

  document.addEventListener(
    "keydown",
    (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !modalEl.classList.contains("is-open")) return;
      e.preventDefault();
      closeModal();
    },
    true
  );
})();

export {};
